import { getHelpUrl } from '@/lib/urls';

interface ExamplePlot {
  title: string;
  image: string;
  link: string;
}

interface ExamplePlotData {
  title: string;
  imagePath: string;
  link: string;
}

// Example plots data - using actual plot images from docs (not gallery/UI/stats screenshots)
// Note: Images are served from the docs site at /help/img/docs/
// Links point to specific sections in the docs
const examplePlotsData: ExamplePlotData[] = [
  // Biology examples (Oncology Biomarker & Gene Expression)
  {
    title: 'Volcano Plot',
    imagePath: '/img/docs/biology/vocalno.png',
    link: '/examples/bio#1-volcano-plot--week-12-responder-gene-signature',
  },
  {
    title: 'Logistic Curves',
    imagePath: '/img/docs/biology/logistic-curves.png',
    link: '/examples/bio#2-logistic-curves--response-vs-baseline-risk-by-treatment-group',
  },
  {
    title: 'Hexbin',
    imagePath: '/img/docs/biology/hexbin.png',
    link: '/examples/bio#3-faceted-hexbin--baseline-risk-vs-biomarker-change-at-week-12',
  },
  {
    title: 'Ridgeline',
    imagePath: '/img/docs/biology/ridgeline.png',
    link: '/examples/bio#4-ridgeline--biomarker-change-over-time-in-the-high-dose-arm',
  },
  {
    title: 'Violin Plot',
    imagePath: '/img/docs/biology/violin.png',
    link: '/examples/bio#5-violin--biomarker-level-by-treatment-group-average-across-all-timepoints',
  },
  {
    title: 'Heatmap',
    imagePath: '/img/docs/biology/heatmap.png',
    link: '/examples/bio#6-heatmaps--gene-expression-by-gene-timepoint-and-treatment',
  },
  {
    title: 'Dendrogram',
    imagePath: '/img/docs/biology/dendrogram.png',
    link: '/examples/bio#7-gene-dendrogram--week-12-high-dose-co-expression-structure',
  },
  // Economics examples
  {
    title: 'GDP and Inflation',
    imagePath: '/img/docs/economics/line-gdp-inflation.png',
    link: '/examples/economics#1-line-chart--gdp-and-inflation-over-time',
  },
  {
    title: 'GDP Components',
    imagePath: '/img/docs/economics/area-stacked-gdp-components.png',
    link: '/examples/economics#2-area-stacked--gdp-components',
  },
  {
    title: 'Phillips Curve',
    imagePath: '/img/docs/economics/bubble-phillips-growth.png',
    link: '/examples/economics#3-scatter-bubble-size--phillips-curve-with-growth',
  },
  {
    title: 'Treemap',
    imagePath: '/img/docs/economics/treemap-gov-spending.png',
    link: '/examples/economics#4-treemap-chart--government-spending-structure',
  },
  {
    title: 'Population Pyramid',
    imagePath: '/img/docs/education/education-population-pyramid.png',
    link: '/examples/education#1-population-pyramid--who-our-learners-are',
  },
  {
    title: 'Candlestick Chart',
    imagePath: '/img/docs/economics/candlestick-crisis.png',
    link: '/examples/economics#6-candlestick-ohlc--crisis-period-stock-index',
  },
  // Psychology examples
  {
    title: 'Stress Analysis',
    imagePath: '/img/docs/psychology/boxplot-stress-group-session.png',
    link: '/examples/psychology#1-box-plot--stress-by-group-and-session',
  },
  {
    title: 'Stress Change',
    imagePath: '/img/docs/psychology/dumbbell-stress-change.png',
    link: '/examples/psychology#2-dumbbell-plot--change-in-mean-stress-by-group',
  },
  {
    title: 'Reaction Time',
    imagePath: '/img/docs/psychology/kde-reaction-time-facets.png',
    link: '/examples/psychology#3-density-kde--reaction-time-distributions-by-group',
  },
  {
    title: 'RT Improvement',
    imagePath: '/img/docs/psychology/lollipop-rt-improvement.png',
    link: '/examples/psychology#4-lollipop-vertical--reaction-time-improvement-by-group-and-sex',
  },
  {
    title: 'Wellbeing Responders',
    imagePath: '/img/docs/psychology/polar-bar-high-wellbeing.png',
    link: '/examples/psychology#5-polar-area-barpolar--high-wellbeing-responder-rates',
  },
  {
    title: 'Psychological Profile',
    imagePath: '/img/docs/psychology/radar-psychological-profile.png',
    link: '/examples/psychology#6-radar-scatterpolar--psychological-profiles-by-group',
  },
  // Product Analytics examples
  {
    title: 'Funnel Chart',
    imagePath: '/img/docs/product-analytics/funnel-conversion-by-channel.png',
    link: '/examples/product-analytics#1-funnel-chart--where-the-signup-flow-leaks',
  },
  {
    title: 'Sankey Diagram',
    imagePath: '/img/docs/product-analytics/sankey-channel-device-stage.png',
    link: '/examples/product-analytics#2-sankey-diagram--user-flows-across-channel-device-and-outcome',
  },
  {
    title: 'Session Histogram',
    imagePath: '/img/docs/product-analytics/histogram-first-session-activation.png',
    link: '/examples/product-analytics#3-histogram--first-session-duration-by-activation-status',
  },
  {
    title: 'Conversion Bar Chart',
    imagePath: '/img/docs/product-analytics/bar-conversion-by-channel.png',
    link: '/examples/product-analytics#4-vertical-bar-chart--conversion-rate-by-acquisition-channel',
  },
  {
    title: 'Revenue Pie Chart',
    imagePath: '/img/docs/product-analytics/pie-mrr-by-plan.png',
    link: '/examples/product-analytics#5-pie-chart--revenue-mix-by-subscription-plan',
  },
  {
    title: 'Gantt Chart',
    imagePath: '/img/docs/product-analytics/gantt-project-timeline.png',
    link: '/examples/product-analytics#6-gantt-chart--roadmap-context-for-product-changes',
  },
  // Climate examples
  {
    title: 'Sector Emissions',
    imagePath: '/img/docs/climate/chart1-sector-emissions.png',
    link: '/examples/climate#1-grouped-horizontal-bar--sector-emissions',
  },
  {
    title: 'Energy Mix',
    imagePath: '/img/docs/climate/chart2-energy-mix.png',
    link: '/examples/climate#2-normalized-vertical-bar--regional-energy-mix',
  },
  {
    title: '100% Stacked Bar',
    imagePath: '/img/docs/education/education-satisfaction-by-modality.png',
    link: '/examples/education#2-100-stacked-horizontal-bar--satisfaction-by-learning-modality',
  },
  {
    title: 'Study Hours vs Score',
    imagePath: '/img/docs/education/education-scatter-study-hours-vs-score.png',
    link: '/examples/education#3-scatter-plot--study-hours-vs-final-exam-performance',
  },
  {
    title: 'Carbon Trade',
    imagePath: '/img/docs/climate/chart5-carbon-trade-chord.png',
    link: '/examples/climate#5-filled-chord-diagram--embodied-carbon-trade-flows',
  },
  {
    title: 'Temperature Anomalies',
    imagePath: '/img/docs/climate/chart6-temp-anomalies.png',
    link: '/examples/climate#6-multi-line-time-series--temperature-anomalies-vs-15-c',
  },
  // Education examples
  {
    title: 'Bullet Graphs',
    imagePath: '/img/docs/education/education-bullets-kpi-performance.png',
    link: '/examples/education#6-bullet-graphs--kpi-performance-vs-targets',
  },
  {
    title: 'Doughnut Chart',
    imagePath: '/img/docs/education/education-doughnut-mrr-by-plan.png',
    link: '/examples/education#5-doughnut-chart--paid-mrr-mix-by-plan-type',
  },
];

export default function ExamplePlotsCarousel() {
  // Build example plots with full URLs
  const examplePlots: ExamplePlot[] = examplePlotsData.map(plot => ({
    ...plot,
    image: getHelpUrl(plot.imagePath),
  }));

  // Split into three different sets with no repeats
  // Row 1: First 12 plots (Biology + Economics + Psychology)
  // Biology: 7 plots, Economics: 4 plots, Psychology: 1 plot = 12 total
  const row1Plots = examplePlots.slice(0, 12);
  // Row 2: Next 11 plots (Psychology + Product Analytics + Climate)
  // Psychology: 4 plots, Product Analytics: 6 plots, Climate: 1 plot = 11 total
  const row2Plots = examplePlots.slice(12, 23);
  // Row 3: Last 6 plots (Climate + Education)
  // Climate: 2 plots, Education: 4 plots = 6 total
  const row3Plots = examplePlots.slice(23);

  // Duplicate items for seamless loop (3x for smooth infinite scroll)
  const duplicatedRow1Plots = [...row1Plots, ...row1Plots, ...row1Plots];
  const duplicatedRow2Plots = [...row2Plots, ...row2Plots, ...row2Plots];
  const duplicatedRow3Plots = [...row3Plots, ...row3Plots, ...row3Plots];

  // Calculate animation durations to keep the same speed across all rows
  // Duration is proportional to the number of items (since animation moves 1/3 of total width)
  // Row 1: 12 items, 72s baseline
  // Row 2: 11 items → 72s * (11/12) = 66s
  // Row 3: 6 items → 72s * (6/12) = 36s
  const row1Duration = '72s';
  const row2Duration = '66s';
  const row3Duration = '36s';
  return (
    <div className="w-full py-10 overflow-hidden">
      <style>{`
        @keyframes mkt-slide-anim {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        .carousel-row {
          mask-image: linear-gradient(to right, transparent, black 15%, black 85%, transparent);
          -webkit-mask-image: linear-gradient(to right, transparent, black 15%, black 85%, transparent);
        }
        .carousel-slider {
          animation: mkt-slide-anim linear infinite;
          animation-play-state: running;
        }
        .group\/carousel:hover .carousel-slider {
          animation-play-state: paused;
        }
      `}</style>
      
      {/* First row - reverse direction */}
      <div className="mb-3 w-full carousel-row overflow-hidden">
        <div className="looper w-full">
          <div className="group/carousel flex w-[fit-content] justify-center">
            <div 
              className="flex w-[max-content] carousel-slider transition-opacity duration-800"
              style={{ animationDuration: row1Duration, animationDirection: 'reverse' }}
            >
              {duplicatedRow1Plots.map((plot, idx) => (
                <div
                  key={`row1-${idx}`}
                  className="mx-1 flex-shrink-0 transition-opacity duration-800 w-[140px] h-[160px] rounded-md bg-secondary p-1.5 text-sm hover:opacity-100 flex flex-col"
                >
                  <a
                    rel="noopener"
                    className="cursor-pointer font-normal w-full h-full break-words hover:underline flex flex-col gap-0.5"
                    target="_blank"
                    href={getHelpUrl(plot.link)}
                  >
                    <img
                      src={plot.image}
                      alt={plot.title}
                      className="w-full h-[90px] object-cover rounded flex-shrink-0"
                      onError={(e) => {
                        // Fallback if image doesn't load
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="flex flex-col gap-0 flex-shrink-0 h-[30px] justify-center">
                      <span className="text-xs leading-tight line-clamp-2 text-left">{plot.title}</span>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        className="self-start mt-0.5"
                      >
                        <path d="M11.3349 10.3301V5.60547L4.47065 12.4707C4.21095 12.7304 3.78895 12.7304 3.52925 12.4707C3.26955 12.211 3.26955 11.789 3.52925 11.5293L10.3945 4.66504H5.66011C5.29284 4.66504 4.99507 4.36727 4.99507 4C4.99507 3.63273 5.29284 3.33496 5.66011 3.33496H11.9999L12.1337 3.34863C12.4369 3.41057 12.665 3.67857 12.665 4V10.3301C12.6649 10.6973 12.3672 10.9951 11.9999 10.9951C11.6327 10.9951 11.335 10.6973 11.3349 10.3301ZM11.333 4.66699L11.3349 4.66797L11.332 4.66504H11.331L11.333 4.66699Z"></path>
                      </svg>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Second row - normal direction */}
      <div className="mb-3 w-full carousel-row overflow-hidden">
        <div className="looper w-full">
          <div className="group/carousel flex w-[fit-content] justify-center">
            <div
              className="flex w-[max-content] carousel-slider transition-opacity duration-800"
              style={{ animationDuration: row2Duration, animationDirection: 'normal' }}
            >
              {duplicatedRow2Plots.map((plot, idx) => (
                <div
                  key={`row2-${idx}`}
                  className="mx-1 flex-shrink-0 transition-opacity duration-800 w-[140px] max-h-[160px] rounded-md bg-secondary p-1.5 text-sm hover:opacity-100 flex flex-col"
                >
                  <a
                    rel="noopener"
                    className="cursor-pointer font-normal w-full h-full break-words hover:underline flex flex-col gap-0.5"
                    target="_blank"
                    href={getHelpUrl(plot.link)}
                  >
                    <img
                      src={plot.image}
                      alt={plot.title}
                      className="w-full flex-1 min-h-0 object-cover rounded flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="flex flex-col gap-0 flex-shrink-0">
                      <span className="text-xs leading-tight">{plot.title}</span>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        className="inline align-text-bottom self-start"
                      >
                        <path d="M11.3349 10.3301V5.60547L4.47065 12.4707C4.21095 12.7304 3.78895 12.7304 3.52925 12.4707C3.26955 12.211 3.26955 11.789 3.52925 11.5293L10.3945 4.66504H5.66011C5.29284 4.66504 4.99507 4.36727 4.99507 4C4.99507 3.63273 5.29284 3.33496 5.66011 3.33496H11.9999L12.1337 3.34863C12.4369 3.41057 12.665 3.67857 12.665 4V10.3301C12.6649 10.6973 12.3672 10.9951 11.9999 10.9951C11.6327 10.9951 11.335 10.6973 11.3349 10.3301ZM11.333 4.66699L11.3349 4.66797L11.332 4.66504H11.331L11.333 4.66699Z"></path>
                      </svg>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Third row - reverse direction */}
      <div className="mb-3 w-full carousel-row overflow-hidden">
        <div className="looper w-full">
          <div className="group/carousel flex w-[fit-content] justify-center">
            <div
              className="flex w-[max-content] carousel-slider transition-opacity duration-800"
              style={{ animationDuration: row3Duration, animationDirection: 'reverse' }}
            >
              {duplicatedRow3Plots.map((plot, idx) => (
                <div
                  key={`row3-${idx}`}
                  className="mx-1 flex-shrink-0 transition-opacity duration-800 w-[140px] h-[160px] rounded-md bg-secondary p-1.5 text-sm hover:opacity-100 flex flex-col"
                >
                  <a
                    rel="noopener"
                    className="cursor-pointer font-normal w-full h-full break-words hover:underline flex flex-col gap-0.5"
                    target="_blank"
                    href={getHelpUrl(plot.link)}
                  >
                    <img
                      src={plot.image}
                      alt={plot.title}
                      className="w-full h-[90px] object-cover rounded flex-shrink-0"
                      onError={(e) => {
                        // Fallback if image doesn't load
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="flex flex-col gap-0 flex-shrink-0 h-[30px] justify-center">
                      <span className="text-xs leading-tight line-clamp-2 text-left">{plot.title}</span>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        className="self-start mt-0.5"
                      >
                        <path d="M11.3349 10.3301V5.60547L4.47065 12.4707C4.21095 12.7304 3.78895 12.7304 3.52925 12.4707C3.26955 12.211 3.26955 11.789 3.52925 11.5293L10.3945 4.66504H5.66011C5.29284 4.66504 4.99507 4.36727 4.99507 4C4.99507 3.63273 5.29284 3.33496 5.66011 3.33496H11.9999L12.1337 3.34863C12.4369 3.41057 12.665 3.67857 12.665 4V10.3301C12.6649 10.6973 12.3672 10.9951 11.9999 10.9951C11.6327 10.9951 11.335 10.6973 11.3349 10.3301ZM11.333 4.66699L11.3349 4.66797L11.332 4.66504H11.331L11.333 4.66699Z"></path>
                      </svg>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

